import { marketsBalancesApi, initialExchangeAssetsState } from '@ultrade/react-sdk';
import { saveUserAssets } from '../redux/_actions/wallet.action';
import { updateMinBalance } from '../redux/_actions/user.action';
import { store } from '../redux/_store/configureStore';
import { ALGORAND_ASSET, amountFormate, AssetType, equalsIgnoreCase, getAssetsImageLinkFromAlgoexplorer } from '../_utils';
import { AccountAssetType, ParsedAccountData } from '../_utils/types';
import algosdk from 'algosdk';
import { BLOCKCHAINS, WH_PROVIDER_CHAIN_ID, getEvmChainId, getWhChainId } from '../wallets/providers';
import { SolanaNetwork } from '../wallets/solana/network';
import { PublicKey } from '@solana/web3.js';
import { TOKEN_PROGRAM_ID, TOKEN_2022_PROGRAM_ID } from '@solana/spl-token';
import { MappedCCTPAssets } from '../redux/_reducers/wallet.reducer';
import { algodClient } from '../App';

export const updateWalletData = async (address: string, chain?: BLOCKCHAINS) => {
  if (!address) return;
  try {
    const codexAssetsData = marketsBalancesApi.endpoints.getCodexAssets.select()(store.getState() as any).data;
    const codexAssets: AccountAssetType[] = Array.isArray(codexAssetsData) ? codexAssetsData : initialExchangeAssetsState;
    const cctpAssets: MappedCCTPAssets = (store.getState() as any).wallet?.cctpAssets || {};
    const allAssets = codexAssets.reduce((acc, asset) => {
      if (cctpAssets[asset.index]) {
        for (const cctpAsset of cctpAssets[asset.index]) {
          acc.push({...asset, index: cctpAsset.address, chainId: cctpAsset.chainId });
        }
      } else {
        acc.push(asset);
      }
      return acc;
    }, [] as AccountAssetType[]);
    const assetIndexes = allAssets.reduce((acc, a) => {
      if (a.chainId === WH_PROVIDER_CHAIN_ID[chain]) acc.add(a.index);
      return acc;
    }, new Set<string>());

    const isAlgorand = algosdk.isValidAddress(address);
    const isSolana = !isAlgorand && !address.startsWith('0x');

    if (isAlgorand) {
      const account = await algodClient.accountInformation(address).do();
      const minBalance = account['min-balance'];
      const uniqAssets = formatAlgprandAssets(account, allAssets, assetIndexes);

      if (minBalance) store.dispatch(updateMinBalance(minBalance));
      store.dispatch(saveUserAssets(uniqAssets));
      
    } else if (isSolana) {
      let parsedData = await getSolanaAccountBalances(address);

      if (!parsedData) parsedData = [];

      if (parsedData.length === 0) {
        parsedData.push({
        info: {
          mint: "So11111111111111111111111111111111111111112",
          tokenAmount: {
            amount: "0",
            decimals: 9,
            uiAmount: 0,
            uiAmountString: "0"
          },
          isNative: true,
          owner: address,
          state: "initialized"
        }, 
        type: "account",
      });
    }

      const uniqAssets = parsedData.filter(a => assetIndexes.has(a.info.mint));
      const formattedAssets = uniqAssets.map(asset => formatSolanaAsset(allAssets, {
        amount: asset.info.tokenAmount.amount,
        index: asset.info.mint
      }));

      uniqAssets.length && store.dispatch(saveUserAssets(formattedAssets))

    } else {
      const activeEvmChainId = getEvmChainId(chain);
      if (!activeEvmChainId) return;

      const activeWhChainId: number = getWhChainId(chain);
      const uniqAssetArr = [...assetIndexes] as `0x${string}`[];
      const walletAssetsWhithId = getEvmAssets(uniqAssetArr, activeWhChainId, allAssets);

      walletAssetsWhithId && store.dispatch(saveUserAssets(walletAssetsWhithId));
    }

  } catch (e) {
    console.log('Error on update wallet data: ', e);
  }
};

const getSolanaAccountBalances = async (userAddress: string): Promise<ParsedAccountData[]> => {
  const userPublicKey = new PublicKey(userAddress);
  try {
    const accounts2 = await SolanaNetwork.connection.getParsedTokenAccountsByOwner(userPublicKey, {
      programId: TOKEN_2022_PROGRAM_ID,
    });
    const accounts = await SolanaNetwork.connection.getParsedTokenAccountsByOwner(userPublicKey, {
      programId: TOKEN_PROGRAM_ID,
    });
  
    if (!accounts) return;

    const parsedData = accounts.value.concat(accounts2.value).map(a => a.account.data.parsed) as ParsedAccountData[];
    return parsedData;
  } catch (error) {
    console.log('Failed to load solana account balances', error);
  }
};

const getEvmAssets = (addressArray: `0x${string}`[], activeWhChainId: number, allAssets: AccountAssetType[]): AssetType[] => {
  return addressArray.map(ind => {
    const codexAsset = allAssets.find(e => equalsIgnoreCase(e.index, ind) && e.chainId === activeWhChainId);
    return {
      id: codexAsset.id,
      index: codexAsset.index,
      name: codexAsset.name,
      unit_name: codexAsset.isWrapped ? `W${codexAsset.unit_name}` : codexAsset.unit_name,
      img: codexAsset.img,
      decimal: codexAsset.decimal,
      usd_value: codexAsset.usd_value ?? 0,
      amount: 0n.toString(),
      chainId: activeWhChainId
    }
  })
}

const formatSolanaAsset = (allAssets: AccountAssetType[], asset: { index: string, amount: string }): AssetType => {
  const assetFromCodex = allAssets.find(e => equalsIgnoreCase(e.index, asset.index));
  const formattedAsset = {
    id: assetFromCodex.id,
    index: asset.index,
    amount: asset.amount,
    decimal: assetFromCodex.decimal,
    name: assetFromCodex.name,
    unit_name: assetFromCodex.unit_name,
    img: assetFromCodex.img,
    usd_value: assetFromCodex.usd_value ?? 0,
    chainId: WH_PROVIDER_CHAIN_ID[BLOCKCHAINS.SOLANA]
  };
  return formattedAsset;
}

export const formatAlgprandAssets = (accountInfo, allAssets: AccountAssetType[], assetIndexes: Set<string>): AssetType[] => {
  const acc_assets = accountInfo.assets as {amount: number, ['asset-id']: number, ['is-frozen']: boolean}[];
  const assetFromCodex = allAssets.find(e => equalsIgnoreCase(e.index, ALGORAND_ASSET.index));
  const algo = {
    ...ALGORAND_ASSET,
    id: assetFromCodex.id,
    amount: accountInfo?.amount?.toString(),
    usd_value: 0,
  };
  const walletAssets: AssetType[] = [algo];

  if (acc_assets && acc_assets.length > 0) {
    const uniqAssets = acc_assets.filter(a => assetIndexes.has(a['asset-id'].toString()));
    if (uniqAssets.length) {
      for (const asset of uniqAssets) {
        const assetFromCodex = allAssets.find(e => e.index === asset['asset-id'].toString());
        if (assetFromCodex) {
          const updatedAsset = {
            id: assetFromCodex.id,
            index: assetFromCodex.index,
            name: assetFromCodex.name,
            unit_name: assetFromCodex.unit_name,
            img: assetFromCodex.img || getAssetsImageLinkFromAlgoexplorer(assetFromCodex.index),
            decimal: assetFromCodex.decimal,
            usd_value: assetFromCodex.usd_value ?? 0,
            amount: asset?.amount?.toString(),
            chainId: WH_PROVIDER_CHAIN_ID[BLOCKCHAINS.ALGORAND]
          };
          walletAssets.push(updatedAsset);
        }
      }
    }
  }
  return walletAssets;
};
